import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Modal, Dropdown } from 'react-bootstrap';

const StoreClientsAdmin = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedListFilter, setSelectedListFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modal de Edición / Creación
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingClient, setEditingClient] = useState({
    id: null,
    d_razon: '',
    d_email: '',
    d_whatsapp: '',
    n_telef: '',
    n_celul: '',
    d_passnu: '',
    d_domic: '',
    d_local: '',
    c_posta: '',
    c_clien: 0,
    n_lista: 1,
    c_situa: 1,
    d_obser: ''
  });
  const [savingClient, setSavingClient] = useState(false);

  // Búsqueda de clientes ERP para vinculación
  const [sigecomClients, setSigecomClients] = useState([]);
  const [searchingErp, setSearchingErp] = useState(false);
  const [erpQuery, setErpQuery] = useState('');

  const activeCompanyId = user?.empresa_activa?.c_nuges || user?.empresa_activa?.id || null;

  // 1. Cargar Listas de Precios disponibles
  useEffect(() => {
    const fetchLists = async () => {
      try {
        const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
        const res = await axios.get('/api/listas/', { headers });
        if (Array.isArray(res.data)) {
          setPriceLists(res.data);
        } else if (res.data?.results && Array.isArray(res.data.results)) {
          setPriceLists(res.data.results);
        }
      } catch (err) {
        console.warn('Error cargando listas de precios:', err);
      }
    };
    fetchLists();
  }, [activeCompanyId]);

  // 2. Cargar Clientes de Tienda (Puri000)
  const fetchClients = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize
      };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (selectedListFilter) params.n_lista = selectedListFilter;
      if (statusFilter !== '') params.c_situa = statusFilter;

      const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
      const res = await axios.get('/api/clientes-tienda/', { params, headers });
      if (res.data) {
        if (Array.isArray(res.data)) {
          setClients(res.data);
          setTotalCount(res.data.length);
        } else {
          setClients(res.data.results || []);
          setTotalCount(res.data.count || 0);
        }
      } else {
        setClients([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.warn('Error cargando clientes de tienda:', err);
      setClients([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedListFilter, statusFilter, pageSize, activeCompanyId]);

  useEffect(() => {
    fetchClients(currentPage);
  }, [fetchClients, currentPage]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchClients(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedListFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  // Abrir modal de creación
  const handleOpenCreateModal = () => {
    setIsEditing(false);
    setEditingClient({
      id: null,
      d_razon: '',
      d_email: '',
      d_whatsapp: '',
      n_telef: '',
      n_celul: '',
      d_passnu: '1234',
      d_domic: '',
      d_local: '',
      c_posta: '',
      c_clien: 0,
      n_lista: priceLists.length > 0 ? Number(priceLists[0].n_lista) : 1,
      c_situa: 1,
      d_obser: ''
    });
    setErpQuery('');
    setSigecomClients([]);
    setShowEditModal(true);
  };

  // Abrir modal de edición
  const handleOpenEditModal = (client) => {
    setIsEditing(true);
    setEditingClient({
      id: client.id,
      d_razon: client.d_razon || '',
      d_email: client.d_email || '',
      d_whatsapp: client.d_whatsapp || '',
      n_telef: client.n_telef || '',
      n_celul: client.n_celul || '',
      d_passnu: client.d_passnu || '',
      d_domic: client.d_domic || '',
      d_local: client.d_local || '',
      c_posta: client.c_posta || '',
      c_clien: client.c_clien || 0,
      n_lista: client.n_lista || 1,
      c_situa: client.c_situa !== undefined ? client.c_situa : 1,
      d_obser: client.d_obser || ''
    });
    setErpQuery(client.cliente_sigecom_nombre ? `${client.c_clien} - ${client.cliente_sigecom_nombre}` : (client.c_clien ? String(client.c_clien) : ''));
    setSigecomClients([]);
    setShowEditModal(true);
  };

  // Buscar clientes en ERP para vinculación
  const handleSearchErpClients = async (query) => {
    setErpQuery(query);
    if (!query || query.trim().length < 2) {
      setSigecomClients([]);
      return;
    }
    setSearchingErp(true);
    try {
      const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
      const res = await axios.get('/api/clientes/', { params: { search: query.trim() }, headers });
      const list = Array.isArray(res.data) ? res.data : (res.data.results || []);
      setSigecomClients(list.slice(0, 8));
    } catch (err) {
      console.warn('Error buscando clientes ERP:', err);
    } finally {
      setSearchingErp(false);
    }
  };

  // Guardar Cambios (Crear o Actualizar)
  const handleSaveClient = async (e) => {
    e.preventDefault();
    if (!editingClient.d_email || !editingClient.d_email.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Email requerido',
        text: 'El correo electrónico es obligatorio para el acceso del cliente.'
      });
      return;
    }

    setSavingClient(true);
    try {
      const payload = {
        d_razon: editingClient.d_razon.trim(),
        d_email: editingClient.d_email.trim().toLowerCase(),
        d_whatsapp: editingClient.d_whatsapp.trim(),
        n_telef: editingClient.n_telef.trim(),
        n_celul: editingClient.n_celul.trim(),
        d_passnu: editingClient.d_passnu.trim(),
        d_domic: editingClient.d_domic.trim(),
        d_local: editingClient.d_local.trim(),
        c_posta: editingClient.c_posta.trim(),
        c_clien: Number(editingClient.c_clien || 0),
        n_lista: Number(editingClient.n_lista || 1),
        c_situa: Number(editingClient.c_situa),
        d_obser: editingClient.d_obser.trim()
      };

      const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};

      if (editingClient?.id) {
        const res = await axios.patch(`/api/clientes-tienda/${editingClient.id}/`, payload, { headers });
        try {
          const saved = localStorage.getItem('sigecom_ecom_customer');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed.id === editingClient.id || parsed.d_email === payload.d_email) {
              const updated = {
                ...parsed,
                ...(res.data || {}),
                ...payload
              };
              localStorage.setItem('sigecom_ecom_customer', JSON.stringify(updated));
            }
          }
        } catch (e) {}
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Cliente actualizado correctamente',
          showConfirmButton: false,
          timer: 2000
        });
      } else {
        await axios.post('/api/clientes-tienda/', payload, { headers });
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Cliente creado correctamente',
          showConfirmButton: false,
          timer: 2000
        });
      }
      setShowEditModal(false);
      fetchClients(currentPage);
    } catch (err) {
      console.error('Error guardando cliente:', err);
      const errMsg = err.response?.data?.d_email?.[0] || err.response?.data?.error || 'No se pudo guardar la información del cliente.';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errMsg
      });
    } finally {
      setSavingClient(false);
    }
  };

  // Cambio rápido de Lista de Precios
  const handleQuickChangeList = async (client, newLista) => {
    try {
      const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
      const res = await axios.patch(`/api/clientes-tienda/${client.id}/`, { n_lista: Number(newLista) }, { headers });
      try {
        const saved = localStorage.getItem('sigecom_ecom_customer');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.id === client.id || parsed.d_email === client.d_email) {
            const updated = {
              ...parsed,
              ...(res.data || {}),
              n_lista: Number(newLista)
            };
            localStorage.setItem('sigecom_ecom_customer', JSON.stringify(updated));
          }
        }
      } catch (e) {}
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `Lista ${newLista} asignada a ${client.d_razon || client.d_email}`,
        showConfirmButton: false,
        timer: 2000
      });
      fetchClients(currentPage);
    } catch (err) {
      console.error('Error asignando lista:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar la lista de precios.'
      });
    }
  };

  // Toggle de Estado Activo / Inactivo
  const handleToggleStatus = async (client) => {
    const newStatus = client.c_situa === 1 || client.c_situa === 0 ? 9 : 1;
    const actionText = newStatus === 9 ? 'desactivar' : 'activar';

    const result = await Swal.fire({
      title: `¿Desea ${actionText} este cliente?`,
      text: newStatus === 9 
        ? `El cliente ${client.d_razon || client.d_email} no podrá acceder a la tienda online.`
        : `El cliente ${client.d_razon || client.d_email} volverá a tener acceso normal.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus === 9 ? '#d33' : '#198754',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Sí, ${actionText}`,
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
        await axios.patch(`/api/clientes-tienda/${client.id}/`, { c_situa: newStatus }, { headers });
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: `Cliente ${newStatus === 9 ? 'desactivado' : 'activado'} correctamente`,
          showConfirmButton: false,
          timer: 2000
        });
        fetchClients(currentPage);
      } catch (err) {
        console.error('Error cambiando estado:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cambiar el estado del cliente.'
        });
      }
    }
  };

  // Eliminar Cliente
  const handleDeleteClient = async (client) => {
    const result = await Swal.fire({
      title: '¿Eliminar cliente de la tienda?',
      text: `Se borrará la cuenta de ${client.d_razon || client.d_email}. Esta acción no se puede deshacer.`,
      icon: 'error',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const headers = activeCompanyId ? { 'X-Company-ID': String(activeCompanyId) } : {};
        await axios.delete(`/api/clientes-tienda/${client.id}/`, { headers });
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Cliente eliminado correctamente',
          showConfirmButton: false,
          timer: 2000
        });
        fetchClients(currentPage);
      } catch (err) {
        console.error('Error eliminando cliente:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo eliminar el cliente.'
        });
      }
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  return (
    <div className="container-fluid px-3 px-lg-4 py-4">
      
      {/* 1. HEADER DE LA PÁGINA */}
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4 bg-white p-4 rounded-4 shadow-sm border">
        <div className="d-flex align-items-center gap-3">
          <div className="bg-primary text-white rounded-3 p-3 d-flex align-items-center justify-content-center shadow-xs">
            <i className="bi bi-people-fill fs-2"></i>
          </div>
          <div>
            <div className="d-flex align-items-center gap-2">
              <h4 className="fw-black text-dark mb-0">Clientes de Tienda Online</h4>
              <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-2.5 py-1 small">
                {totalCount} Registrados
              </span>
            </div>
            <p className="text-muted small mb-0 mt-1">
              Gestioná las cuentas de usuarios de la tienda, asigná <strong>listas de precios diferenciadas</strong> y editá sus datos de contacto.
            </p>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2 shadow-2xs"
            onClick={() => fetchClients(currentPage)}
            title="Refrescar lista de clientes"
          >
            <i className="bi bi-arrow-clockwise fs-6"></i>
            <span>Actualizar</span>
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm rounded-pill px-3 py-2 fw-bold text-white d-inline-flex align-items-center gap-2 shadow-2xs"
            onClick={handleOpenCreateModal}
          >
            <i className="bi bi-person-plus-fill fs-6"></i>
            <span>Nuevo Cliente</span>
          </button>
        </div>
      </div>

      {/* 2. BARRA DE BÚSQUEDA Y FILTROS */}
      <div className="card border-0 shadow-sm rounded-4 bg-white mb-4">
        <div className="card-body p-3 p-lg-4">
          <form onSubmit={handleSearchSubmit} className="row g-3 align-items-end">
            
            {/* Buscador de Texto */}
            <div className="col-12 col-md-5">
              <label className="form-label small fw-bold text-muted mb-1">
                Buscar Cliente (Razón Social, Email, Teléfono, WhatsApp o ID)
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0 text-muted">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Ej: nicolas, ventas@empresa.com, 112297..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Filtro por Lista de Precios */}
            <div className="col-12 col-sm-6 col-md-3">
              <label className="form-label small fw-bold text-muted mb-1">
                Filtrar por Lista de Precios
              </label>
              <select
                className="form-select"
                value={selectedListFilter}
                onChange={(e) => {
                  setSelectedListFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todas las Listas</option>
                {priceLists.map((pl) => (
                  <option key={pl.n_lista} value={pl.n_lista}>
                    Lista {pl.n_lista} {pl.d_obser ? `- ${pl.d_obser}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro por Estado */}
            <div className="col-12 col-sm-6 col-md-2">
              <label className="form-label small fw-bold text-muted mb-1">
                Estado
              </label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos</option>
                <option value="1">Activos</option>
                <option value="9">Inactivos / Bloqueados</option>
              </select>
            </div>

            {/* Botones de Filtro */}
            <div className="col-12 col-md-2 d-flex gap-2">
              <button
                type="submit"
                className="btn btn-primary w-100 rounded-pill fw-bold text-white shadow-2xs"
              >
                <i className="bi bi-funnel-fill me-1"></i> Filtrar
              </button>
              {(searchTerm || selectedListFilter || statusFilter !== '') && (
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-pill"
                  onClick={handleClearFilters}
                  title="Limpiar filtros"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>

          </form>
        </div>
      </div>

      {/* 3. TABLA DE CLIENTES */}
      <div className="card border-0 shadow-sm rounded-4 bg-white" style={{ minHeight: '400px' }}>
        <div className="table-responsive" style={{ overflow: 'visible', minHeight: '350px', paddingBottom: '140px' }}>
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light text-muted small text-uppercase">
              <tr>
                <th className="ps-4 py-3" style={{ minWidth: '220px' }}>Cliente / Razón Social</th>
                <th style={{ minWidth: '200px' }}>Contacto & WhatsApp</th>
                <th style={{ minWidth: '160px' }}>Ubicación</th>
                <th style={{ minWidth: '220px' }}>Lista de Precios Asignada</th>
                <th style={{ minWidth: '160px' }}>Cliente SISGECOM</th>
                <th style={{ minWidth: '100px' }}>Estado</th>
                <th className="pe-4 text-end" style={{ minWidth: '130px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <div className="text-muted small mt-2">Cargando clientes de tienda...</div>
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <div 
                      className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center text-muted mb-3 shadow-xs"
                      style={{ width: '64px', height: '64px' }}
                    >
                      <i className="bi bi-people fs-2 opacity-50"></i>
                    </div>
                    <h6 className="fw-bold text-dark">No se encontraron clientes</h6>
                    <p className="text-muted small mb-3">No hay clientes que coincidan con los filtros aplicados.</p>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary rounded-pill px-3"
                      onClick={handleClearFilters}
                    >
                      Limpiar filtros
                    </button>
                  </td>
                </tr>
              ) : (
                clients.map((client) => {
                  const isActive = client.c_situa !== 9;
                  return (
                    <tr key={client.id} className={!isActive ? 'table-light opacity-75' : ''}>
                      
                      {/* Columna 1: Cliente / Razón Social */}
                      <td className="ps-4 py-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-xs flex-shrink-0 ${
                              isActive ? 'bg-primary' : 'bg-secondary'
                            }`}
                            style={{ width: '40px', height: '40px', fontSize: '1.05rem' }}
                          >
                            {(client.d_razon || client.d_email || 'C').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-bold text-dark fs-6 d-flex align-items-center gap-2">
                              <span>{client.d_razon || 'Sin Nombre Registrado'}</span>
                              <span className="badge bg-light text-muted border rounded-pill font-monospace text-2xs">
                                ID: #{client.id}
                              </span>
                            </div>
                            <div className="text-muted small d-flex align-items-center gap-2">
                              <span><i className="bi bi-envelope me-1 text-primary"></i>{client.d_email}</span>
                            </div>
                            {client.f_alta && (
                              <div className="text-muted text-2xs">
                                Alta: {client.f_alta}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Columna 2: Contacto & WhatsApp */}
                      <td>
                        <div className="d-flex flex-column gap-1 small">
                          {client.d_whatsapp ? (
                            <a
                              href={`https://wa.me/${client.d_whatsapp.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-decoration-none text-success fw-semibold d-flex align-items-center gap-1 hover-opacity"
                            >
                              <i className="bi bi-whatsapp"></i>
                              <span>{client.d_whatsapp}</span>
                            </a>
                          ) : (
                            <span className="text-muted fst-italic text-xs">Sin WhatsApp</span>
                          )}
                          {client.n_telef && (
                            <span className="text-muted">
                              <i className="bi bi-telephone me-1 text-secondary"></i>
                              {client.n_telef}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Columna 3: Ubicación */}
                      <td>
                        <div className="small">
                          {client.d_local || client.d_domic ? (
                            <>
                              <div className="fw-semibold text-dark text-truncate" style={{ maxWidth: '160px' }}>
                                {client.d_local || 'Sin Localidad'}
                              </div>
                              <div className="text-muted text-xs text-truncate" style={{ maxWidth: '160px' }}>
                                {client.d_domic} {client.c_posta ? `(${client.c_posta})` : ''}
                              </div>
                            </>
                          ) : (
                            <span className="text-muted fst-italic text-xs">Sin domicilio cargado</span>
                          )}
                        </div>
                      </td>

                      {/* Columna 4: Lista de Precios Asignada */}
                      <td>
                        <Dropdown align="start" className="d-inline-block">
                          <Dropdown.Toggle
                            as="button"
                            className="btn btn-sm btn-outline-primary rounded-pill px-3 py-1.5 fw-bold d-inline-flex align-items-center gap-2 shadow-2xs text-nowrap"
                            id={`dropdown-price-list-${client.id}`}
                            title="Hacé clic para cambiar la lista de precios"
                          >
                            <i className="bi bi-tags-fill text-warning"></i>
                            <span>{client.nombre_lista || `Lista ${client.n_lista || 1}`}</span>
                            <span className="badge bg-primary text-white rounded-circle p-1 ms-1 d-inline-flex align-items-center justify-content-center" style={{ width: '18px', height: '18px' }}>
                              <i className="bi bi-pencil-fill" style={{ fontSize: '0.65rem' }}></i>
                            </span>
                          </Dropdown.Toggle>

                          <Dropdown.Menu 
                            className="shadow-lg border-0 rounded-4 p-2 mt-1" 
                            style={{ minWidth: '260px', zIndex: 1060 }}
                          >
                            <div className="dropdown-header small text-uppercase fw-bold text-muted px-2 py-1">
                              <i className="bi bi-tags-fill me-1 text-primary"></i> Asignar Lista de Precios:
                            </div>
                            {priceLists.map((pl) => (
                              <Dropdown.Item
                                key={pl.n_lista}
                                active={Number(client.n_lista) === Number(pl.n_lista)}
                                onClick={() => handleQuickChangeList(client, pl.n_lista)}
                                className="rounded-3 py-2 px-3 small d-flex align-items-center justify-content-between my-0.5"
                              >
                                <span className="fw-semibold">
                                  Lista {pl.n_lista} {pl.d_obser ? `- ${pl.d_obser}` : ''}
                                </span>
                                {Number(client.n_lista) === Number(pl.n_lista) && (
                                  <i className="bi bi-check-circle-fill text-primary ms-2"></i>
                                )}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                        <div className="text-muted text-2xs mt-1">
                          El cliente ve solo los precios de esta lista.
                        </div>
                      </td>

                      {/* Columna 5: Cliente SIGECOM */}
                      <td>
                        {client.c_clien && Number(client.c_clien) > 0 ? (
                          <div className="small">
                            <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-2.5 py-1 fw-bold">
                              <i className="bi bi-link-45deg me-1"></i>
                              Cod #{client.c_clien}
                            </span>
                            {client.cliente_sigecom_nombre && (
                              <div className="text-dark fw-semibold text-xs mt-0.5 text-truncate" style={{ maxWidth: '160px' }}>
                                {client.cliente_sigecom_nombre}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="badge bg-light text-muted border rounded-pill px-2 py-1 small">
                            No vinculado
                          </span>
                        )}
                      </td>

                      {/* Columna 6: Estado */}
                      <td>
                        {isActive ? (
                          <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-2.5 py-1 small">
                            ● Activo
                          </span>
                        ) : (
                          <span className="badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill px-2.5 py-1 small">
                            ✕ Inactivo
                          </span>
                        )}
                      </td>

                      {/* Columna 7: Acciones */}
                      <td className="pe-4 text-end">
                        <div className="btn-group btn-group-sm shadow-2xs rounded-pill overflow-hidden border">
                          <button
                            type="button"
                            className="btn btn-light text-primary"
                            onClick={() => handleOpenEditModal(client)}
                            title="Editar Datos y Lista de Precios"
                          >
                            <i className="bi bi-pencil-square"></i>
                          </button>
                          <button
                            type="button"
                            className={`btn btn-light ${isActive ? 'text-warning' : 'text-success'}`}
                            onClick={() => handleToggleStatus(client)}
                            title={isActive ? 'Desactivar acceso a la tienda' : 'Activar acceso a la tienda'}
                          >
                            <i className={`bi ${isActive ? 'bi-lock-fill' : 'bi-unlock-fill'}`}></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-light text-danger"
                            onClick={() => handleDeleteClient(client)}
                            title="Eliminar cliente"
                          >
                            <i className="bi bi-trash3"></i>
                          </button>
                        </div>
                      </td>

                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 4. PIE DE TABLA Y PAGINACIÓN */}
        <div className="card-footer bg-white border-top py-3 px-4 d-flex flex-column flex-sm-row align-items-center justify-content-between gap-2">
          <div className="text-muted small">
            Mostrando <strong>{clients.length}</strong> de <strong>{totalCount}</strong> clientes registrados
          </div>

          {totalPages > 1 && (
            <nav aria-label="Paginación de clientes">
              <ul className="pagination pagination-sm mb-0 gap-1">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: '32px', height: '32px' }}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                    <button
                      className="page-link rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: '32px', height: '32px' }}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: '32px', height: '32px' }}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>

      {/* 5. MODAL DE EDICIÓN / CREACIÓN */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-light border-0 py-3 px-4">
          <Modal.Title className="fw-bold fs-5 text-dark d-flex align-items-center gap-2">
            <i className={`bi ${isEditing ? 'bi-pencil-square text-primary' : 'bi-person-plus-fill text-success'}`}></i>
            <span>{isEditing ? `Editar Cliente: ${editingClient.d_razon || editingClient.d_email}` : 'Nuevo Cliente de Tienda Online'}</span>
          </Modal.Title>
        </Modal.Header>

        <form onSubmit={handleSaveClient}>
          <Modal.Body className="p-4">
            
            {/* SECCIÓN 1: ASIGNACIÓN DE LISTA DE PRECIOS */}
            <div className="p-3 bg-primary-subtle border border-primary-subtle rounded-4 mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-tags-fill text-primary fs-5"></i>
                <h6 className="fw-bold text-dark mb-0">Lista de Precios Asignada al Cliente</h6>
              </div>
              <p className="text-muted small mb-3">
                Esta lista define <strong>estrictamente los precios que el cliente verá y descargará</strong> al navegar en la tienda online.
              </p>

              <div>
                <label className="form-label small fw-bold text-dark mb-1">
                  Seleccionar Lista de Precios
                </label>
                <select
                  className="form-select form-select-lg fw-bold border-primary shadow-xs"
                  value={editingClient.n_lista}
                  onChange={(e) => setEditingClient({ ...editingClient, n_lista: Number(e.target.value) })}
                  required
                >
                  {priceLists.map((pl) => (
                    <option key={pl.n_lista} value={pl.n_lista}>
                      Lista {pl.n_lista} {pl.d_obser ? `- ${pl.d_obser}` : ''} {pl.l_prede ? '(Predeterminada)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* SECCIÓN 2: DATOS DE ACCESO Y CONTACTO */}
            <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">
              <i className="bi bi-person-lines-fill me-2 text-primary"></i> Datos Principales y Contacto
            </h6>

            <div className="row g-3 mb-4">
              {/* Razón Social / Nombre */}
              <div className="col-12 col-md-6">
                <label className="form-label small fw-bold text-muted mb-1">
                  Razón Social / Nombre Completo
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editingClient.d_razon}
                  onChange={(e) => setEditingClient({ ...editingClient, d_razon: e.target.value })}
                  placeholder="Ej: Distribuidora Norte S.R.L. / Juan Pérez"
                />
              </div>

              {/* Email (Login) */}
              <div className="col-12 col-md-6">
                <label className="form-label small fw-bold text-muted mb-1">
                  Correo Electrónico (Usuario de Login) *
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={editingClient.d_email}
                  onChange={(e) => setEditingClient({ ...editingClient, d_email: e.target.value })}
                  placeholder="cliente@ejemplo.com"
                  required
                />
              </div>

              {/* Contraseña Web */}
              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold text-muted mb-1">
                  Contraseña Web
                </label>
                <input
                  type="text"
                  className="form-control font-monospace"
                  value={editingClient.d_passnu}
                  onChange={(e) => setEditingClient({ ...editingClient, d_passnu: e.target.value })}
                  placeholder="Contraseña del cliente"
                />
                <span className="text-muted text-2xs">Visible para soporte del cliente.</span>
              </div>

              {/* WhatsApp */}
              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold text-muted mb-1">
                  WhatsApp (con código de área)
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light text-success fw-bold">
                    <i className="bi bi-whatsapp"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control font-monospace"
                    value={editingClient.d_whatsapp}
                    onChange={(e) => setEditingClient({ ...editingClient, d_whatsapp: e.target.value })}
                    placeholder="1122971931"
                  />
                </div>
              </div>

              {/* Teléfono Fijo */}
              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold text-muted mb-1">
                  Teléfono Fijo / Alternativo
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editingClient.n_telef}
                  onChange={(e) => setEditingClient({ ...editingClient, n_telef: e.target.value })}
                  placeholder="(011) 4247-6622"
                />
              </div>
            </div>

            {/* SECCIÓN 3: DOMICILIO Y UBICACIÓN */}
            <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">
              <i className="bi bi-geo-alt-fill me-2 text-danger"></i> Domicilio y Localidad
            </h6>

            <div className="row g-3 mb-4">
              <div className="col-12 col-md-5">
                <label className="form-label small fw-bold text-muted mb-1">Dirección / Domicilio</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={editingClient.d_domic}
                  onChange={(e) => setEditingClient({ ...editingClient, d_domic: e.target.value })}
                  placeholder="Vicente Damonte 1237"
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold text-muted mb-1">Localidad</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={editingClient.d_local}
                  onChange={(e) => setEditingClient({ ...editingClient, d_local: e.target.value })}
                  placeholder="Lanús Este"
                />
              </div>

              <div className="col-12 col-md-3">
                <label className="form-label small fw-bold text-muted mb-1">Código Postal</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={editingClient.c_posta}
                  onChange={(e) => setEditingClient({ ...editingClient, c_posta: e.target.value })}
                  placeholder="B1824ALG"
                />
              </div>
            </div>

            {/* SECCIÓN 4: VINCULACIÓN CON CLIENTE ERP & ESTADO */}
            <h6 className="fw-bold text-dark mb-3 border-bottom pb-2">
              <i className="bi bi-link-45deg me-2 text-success"></i> Vinculación con ERP SISGECOM & Estado
            </h6>

            <div className="row g-3">
              {/* Vinculación ERP */}
              <div className="col-12 col-md-8">
                <label className="form-label small fw-bold text-muted mb-1">
                  Vincular con Cuenta Corriente / Cliente SISGECOM (Opcional)
                </label>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Escribí para buscar cliente en SISGECOM por nombre o código..."
                    value={erpQuery}
                    onChange={(e) => handleSearchErpClients(e.target.value)}
                  />
                  {searchingErp && (
                    <span className="position-absolute top-50 end-0 translate-middle-y pe-3">
                      <span className="spinner-border spinner-border-sm text-primary" role="status"></span>
                    </span>
                  )}
                  {sigecomClients.length > 0 && (
                    <div className="position-absolute w-100 bg-white border rounded-3 shadow-lg mt-1 p-1 z-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {sigecomClients.map((cli) => (
                        <div
                          key={cli.c_clien}
                          className="p-2 rounded-2 hover-bg-light cursor-pointer d-flex justify-content-between align-items-center small"
                          onClick={() => {
                            setEditingClient({ ...editingClient, c_clien: cli.c_clien });
                            setErpQuery(`${cli.c_clien} - ${cli.d_razon || cli.d_fanta}`);
                            setSigecomClients([]);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div>
                            <strong>#{cli.c_clien}</strong> - {cli.d_razon || cli.d_fanta}
                          </div>
                          <span className="badge bg-light text-muted border">Seleccionar</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="d-flex align-items-center justify-content-between mt-1">
                  <span className="text-muted text-2xs">
                    Código asignado: <strong>{editingClient.c_clien ? `#${editingClient.c_clien}` : 'Ninguno (0)'}</strong>
                  </span>
                  {editingClient.c_clien > 0 && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger text-decoration-none p-0 text-2xs"
                      onClick={() => {
                        setEditingClient({ ...editingClient, c_clien: 0 });
                        setErpQuery('');
                      }}
                    >
                      <i className="bi bi-x-circle me-1"></i> Desvincular
                    </button>
                  )}
                </div>
              </div>

              {/* Estado de la Cuenta */}
              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold text-muted mb-1">
                  Estado de la Cuenta
                </label>
                <select
                  className="form-select form-select-sm fw-semibold"
                  value={editingClient.c_situa}
                  onChange={(e) => setEditingClient({ ...editingClient, c_situa: Number(e.target.value) })}
                >
                  <option value="1">Habilitada (Activo)</option>
                  <option value="9">Bloqueada / Inactiva</option>
                </select>
              </div>

              {/* Observaciones */}
              <div className="col-12">
                <label className="form-label small fw-bold text-muted mb-1">Observaciones / Notas Internas</label>
                <textarea
                  className="form-control form-control-sm"
                  rows="2"
                  value={editingClient.d_obser}
                  onChange={(e) => setEditingClient({ ...editingClient, d_obser: e.target.value })}
                  placeholder="Notas internas sobre este cliente, condiciones acordadas, etc."
                ></textarea>
              </div>
            </div>

          </Modal.Body>

          <Modal.Footer className="bg-light border-0 py-3 px-4">
            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill px-4"
              onClick={() => setShowEditModal(false)}
              disabled={savingClient}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary rounded-pill px-4 fw-bold text-white shadow-2xs d-inline-flex align-items-center gap-2"
              disabled={savingClient}
            >
              {savingClient ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  <i className="bi bi-check2-circle fs-5"></i>
                  <span>Guardar Cliente</span>
                </>
              )}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

    </div>
  );
};

export default StoreClientsAdmin;
